if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      static get observedAttributes() {
        return [...super.observedAttributes, 'active'];
      }

      // The header turns solid with the open panel (search-drawer.liquid).
      // It keys on this body class, set in the same task as [active] so the
      // header changes in the same frame, instead of on
      // body:has(.search-drawer.drawer[active]), which restyled the whole
      // page whenever an element was added anywhere on it.
      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'active') {
          document.body.classList.toggle('search-drawer-active', newValue !== null && this.isConnected);
          return;
        }

        super.attributeChangedCallback(name, oldValue, newValue);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.hasAttribute('active')) document.body.classList.remove('search-drawer-active');
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }

      afterHide() {
        super.afterHide();

        const predictiveSearch = this.querySelector('form[is="predictive-search"]');
        predictiveSearch?.resetSearch();

        this.classList.remove('has-searched');
        this.querySelector('.search__results-scroll')?.scrollTo({ top: 0 });
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.resetSearch({ focus: true });
      }

      resetSearch({ focus = false } = {}) {
        this.abortController?.abort();
        this.abortController = null;
        this.pendingUrl = null;

        this.input.value = '';
        this.endLoading();
        this.removeAttribute('results');

        const results = this.querySelector('[id^="PredictiveSearchResults-"]');
        results?.replaceChildren();

        if (this.typewriter) {
          this.typewriter.removeAttribute('hidden');
        }

        if (focus) {
          this.input.focus();
        }
      }

      endLoading() {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
        this.removeAttribute('loading');
        this.removeAttribute('data-text-loading');
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.abortController = null;
        this.pendingUrl = null;
        this.endLoading();
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.resetSearch();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        if (this.cachedMap.has(url)) {
          // An older query still in flight must not land on top of these
          // (newer) cached results.
          if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
            this.pendingUrl = null;
            this.endLoading();
          }
          this.renderSectionFromCache(url);
        }
        else {
          this.renderSectionFromFetch(url);
        }
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        // The same query is already on its way (e.g. "te" -> "tex" -> "te", or
        // a trailing space, inside one debounce window): let it finish instead
        // of aborting it and sending it again.
        if (this.abortController && this.pendingUrl === url) {
          this.abortController.deduped = true;
          return;
        }

        this.abortController?.abort();
        const controller = this.abortController = new AbortController();
        this.pendingUrl = url;

        this.endLoading();
        this.setAttribute('loading', '');
        this.loadingTimer = setTimeout(() => {
          if (this.isConnected && controller === this.abortController) this.setAttribute('data-text-loading', '');
        }, 2000);

        fetch(url, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            // Superseded (newer query, cache hit or reset) after it arrived.
            if (controller !== this.abortController) return;
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);
            this.abortController = null;
            this.pendingUrl = null;

            this.endLoading();
            this.setAttribute('results', '');
          })
          .catch((error) => {
            const current = controller === this.abortController;
            if (current) {
              this.abortController = null;
              this.pendingUrl = null;
              this.endLoading();
            }

            // An aborted request was superseded by a newer query (each
            // keystroke aborts the last): nothing to report.
            if (error.name !== 'AbortError') {
              console.error(error);

              // Re-typing this query while it was in flight used to abort and
              // re-send it; the dedupe above kept this request instead, so if
              // it fails, send it once more as the re-typed query would have.
              if (current && controller.deduped) this.renderSectionFromFetch(url);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        if (document.getElementById(id) === null) return;

        const results = new DOMParser().parseFromString(responseText, 'text/html').getElementById(id);
        if (!results) throw new Error('Predictive search response is missing results');
        document.getElementById(id).innerHTML = results.innerHTML;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
